import React, { useState } from 'react';
import Input from './Input';
import Button from './Button';
import { GuestContact } from '../services/checkout/guestCheckoutSession';

interface GuestContactFormProps {
  onSubmit: (_contact: GuestContact) => void;
  isSubmitting?: boolean;
  submitLabel?: string;
}

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FieldErrors {
  name?: string;
  email?: string;
}

/**
 * Name + email (required) and phone (optional) collected before a guest can
 * pay. This is the checkout-time equivalent of an account's profile — the
 * backend requires both name and email for a guest checkout (see
 * payment.service.ts's validateGuestContact) since Rental.renterName/
 * renterEmail are required even when renterId is not.
 */
const GuestContactForm: React.FC<GuestContactFormProps> = ({
  onSubmit,
  isSubmitting = false,
  submitLabel = 'Continue',
}) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedName = name.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    const nextErrors: FieldErrors = {};
    if (!trimmedName) {
      nextErrors.name = 'Name is required';
    }
    if (!trimmedEmail) {
      nextErrors.email = 'Email is required';
    } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
      nextErrors.email = 'Please enter a valid email';
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    onSubmit({
      name: trimmedName,
      email: trimmedEmail,
      phone: trimmedPhone || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        id="guest-contact-name"
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        error={errors.name}
        required
        autoComplete="name"
      />
      <Input
        id="guest-contact-email"
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={errors.email}
        required
        autoComplete="email"
        helperText="We'll send your booking confirmation here"
      />
      <Input
        id="guest-contact-phone"
        label="Phone (optional)"
        type="tel"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        autoComplete="tel"
      />
      <Button type="submit" className="w-full" size="lg" isLoading={isSubmitting}>
        {submitLabel}
      </Button>
    </form>
  );
};

export default GuestContactForm;
